import { CartBody, CartItemBody, CartResponse, CartVoucherBody } from "../../sdk/types"
import { eeAddToCart, eeRemovalFromCart } from "../../utils/analytics"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { APIError } from "../../sdk/api"
import { checkAddress } from "../../utils/check-address"
import { errorToast } from "../../utils/error-toast"
import { useAPIClient } from "../../context/client"
import { useEffect } from "react"
import { useLocalesState } from "../../context/locales"
import { useQueryCustomer } from "../customer/use-query-customer"

const useQueryCart = (options?: { background?: boolean; enabled?: boolean }) => {
    const queryClient = useQueryClient()
    const {
        client,
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    const query = useQuery(
        ["getCart", { id: cart_id }],
        () => client.getCart(cart_id as string, { access_token: cart_access_token }),
        {
            enabled: !!(cart_id && cart_access_token && (options?.enabled ? options.enabled : true)),
            meta: { background: options?.background },
        }
    )

    useEffect(() => {
        const error = query.error as APIError
        if (!error || error.code !== "products_unavailable") return

        queryClient.setQueryData<CartResponse>(["getCart", { id: cart_id }], oldData => {
            if (!oldData) {
                client.clearCart()
                return
            }

            errorToast(error)
            return {
                ...oldData,
                items: oldData.items.map(i => {
                    const errorItem = (error.detail as { line_id: string }[]).find(d => d.line_id === i.id)
                    if (errorItem) {
                        return {
                            ...i,
                            error: {
                                code: error.code,
                            },
                        }
                    }
                    return i
                }),
            }
        })
    }, [query.error])

    return query
}

const useQueryHistoricCart = () => {
    const {
        client,
        state: { historic_cart_id, historic_cart_access_token },
    } = useAPIClient()

    return useQuery(
        ["getCart", { id: historic_cart_id }],
        () => client.getCart(historic_cart_id as string, { access_token: historic_cart_access_token }),
        {
            enabled: !!(historic_cart_id && historic_cart_access_token),
            meta: { errorToast: true },
        }
    )
}

const usePostCart = () => {
    const queryClient = useQueryClient()
    const { region } = useLocalesState()
    const { client } = useAPIClient()

    const { data: customerData } = useQueryCustomer()

    return useMutation({
        mutationKey: ["mutateCart"],
        mutationFn: (body: Omit<CartBody, "session_country">) => {
            const bodyWithAddresses = {
                ...body,
                session_country: customerData?.address.country || region,
            }
            if (checkAddress(customerData?.address)) {
                bodyWithAddresses.addresses = {
                    invoice: {
                        id: Number(customerData?.address.id),
                        access_token: customerData?.address.access_token,
                    },
                    shipping: {
                        id: Number(customerData?.address.id),
                        access_token: customerData?.address.access_token,
                    },
                }
            }

            return client.createCart(bodyWithAddresses)
        },
        onSuccess: data => {
            data.items.forEach(i => {
                eeAddToCart(i, i.quantity, i.price.currency.name, data)
            })
            queryClient.setQueryData(["getCart", { id: data.id }], data)
        },
        retry: (failureCount, err) => {
            const error = err as APIError
            return error.type !== "ValidationError" &&
                error.type !== "AccessError" &&
                failureCount <= 2 &&
                error.status !== 404
                ? true
                : false
        },
        retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
    })
}

const useMutationCart = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const {
        client,
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    const { data: cartData } = useQueryCart()

    const bundleInCart = cartData?.items.some(i => i.product.category.type === "bundle")

    const validateCart = (subscription: boolean) => {
        if (!subscription) return

        if (subscription && bundleInCart) {
            throw new APIError("illegal_combination", { code: "illegal_combination_subscription" })
        }
    }

    return useMutation(
        ["mutateCart", { id: cart_id }],
        (body: CartBody) => {
            validateCart(!!body.subscription)

            return client.updateCart(
                cart_id as string,
                body,
                {
                    access_token: cart_access_token,
                },
                language
            )
        },
        {
            onSuccess: data => {
                const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id }])
                queryClient.setQueryData(["getCart", { id: cart_id }], data)
                if (prevData?.addresses.invoice?.id !== data.addresses.invoice?.id) {
                    queryClient.invalidateQueries(["getCartFinanceProviders"])
                }
                if (prevData?.addresses.shipping?.id !== data.addresses.shipping?.id) {
                    queryClient.invalidateQueries(["getCartShippingCarriers"])
                }
            },
            retry: (failureCount, err) => {
                const error = err as APIError
                return error.type !== "ValidationError" &&
                    error.type !== "AccessError" &&
                    error.message !== "illegal_combination" &&
                    failureCount <= 2
                    ? true
                    : false
            },
            retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
        }
    )
}

const usePostCartLine = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const {
        client,
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    const { data: cartData } = useQueryCart()

    const subscriptionInCart = cartData?.items.some(i => i.product.category.type === "subscription")
    const giftInCart = cartData?.items.some(i => i.product.category.type === "gift")

    const validateCart = (category: string) => {
        if (!category) return

        if (category === "bundle" && subscriptionInCart) {
            throw new APIError("illegal_combination", { code: "illegal_combination_bundle" })
        } else if (
            (category !== "gift" && giftInCart) ||
            (category === "gift" && !giftInCart && (cartData?.items.reduce((a, b) => a + b.quantity, 0) || 0) !== 0)
        ) {
            throw new APIError("illegal_combination", { code: "illegal_combination_gift" })
        }
    }

    return useMutation(
        ["mutateCart", { id: cart_id }],
        (body: CartItemBody & { category?: string }) => {
            const { category, ...restBody } = body

            if (category) validateCart(category)

            return client.createCartLine(cart_id as string, restBody, { access_token: cart_access_token }, language)
        },
        {
            onSuccess: data => {
                const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id }])
                data.items.forEach(i => {
                    const prevProduct = prevData?.items.find(prevItem => prevItem.id === i.id)
                    if (!prevProduct) return eeAddToCart(i, i.quantity, i.price.currency.name, data)
                })
                queryClient.setQueryData(["getCart", { id: cart_id }], data)
            },
            retry: (failureCount, err) => {
                const error = err as APIError
                return error.type !== "ValidationError" &&
                    error.type !== "AccessError" &&
                    error.message !== "illegal_combination" &&
                    failureCount <= 2
                    ? true
                    : false
            },
            retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
        }
    )
}

const useMutationCartLine = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const {
        client,
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    return useMutation(
        ["mutateCart", { id: cart_id }],
        ({ id, body }: { id: string; body: CartItemBody & { category?: string } }) => {
            const { category, ...restBody } = body

            return client.updateCartLine(cart_id as string, id, restBody, { access_token: cart_access_token }, language)
        },
        {
            onSuccess: data => {
                const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id }])
                data.items.forEach(i => {
                    const prevProduct = prevData?.items.find(prevItem => prevItem.id === i.id)
                    if (!prevProduct) return eeAddToCart(i, i.quantity, i.price.currency.name, data)

                    const quantityDif = i.quantity - prevProduct.quantity
                    if (quantityDif < 0) {
                        eeRemovalFromCart(i, -quantityDif, i.price.currency.name, data)
                    } else if (quantityDif > 0) {
                        eeAddToCart(i, quantityDif, i.price.currency.name, data)
                    }
                })
                if (!prevData) return
                prevData.items.forEach(prevItem => {
                    const product = data.items.find(i => i.id === prevItem.id)
                    if (product) return
                    eeRemovalFromCart(prevItem, prevItem.quantity, prevItem.price.currency.name, data)
                })
                queryClient.setQueryData(["getCart", { id: cart_id }], data)
            },
            retry: (failureCount, err) => {
                const error = err as APIError
                return error.type !== "ValidationError" && error.type !== "AccessError" && failureCount <= 2
                    ? true
                    : false
            },
            retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
        }
    )
}

const useDeleteCartLine = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const {
        client,
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    return useMutation(
        ["mutateCart", { id: cart_id }],
        ({ id }: { id: string }) => {
            return client.deleteCartLine(cart_id as string, id, { access_token: cart_access_token }, language)
        },
        {
            onSuccess: (_, variables) => {
                const prevData = queryClient.getQueryData<CartResponse>(["getCart", { id: cart_id }])
                if (!prevData) return
                const prevItem = prevData.items.find(i => i.id === variables.id)
                if (!prevItem) return

                const newData = {
                    ...prevData,
                    items: prevData.items.filter(i => i.id !== variables.id),
                    price: {
                        ...prevData.price,
                        total: prevData.price.total - prevItem.price.amount_taxed,
                        pay_now:
                            prevData.price.pay_now -
                            (prevItem.splits?.length ? prevItem.splits[0].price : prevItem.price.amount_taxed),
                    },
                }
                eeRemovalFromCart(prevItem, prevItem.quantity, prevItem.price.currency.name, newData)
                queryClient.setQueryData(["getCart", { id: cart_id }], newData)
                queryClient.invalidateQueries(["getCart", { id: cart_id }])
            },
            retry: (failureCount, err) => {
                const error = err as APIError
                return error.type !== "ValidationError" && error.type !== "AccessError" && failureCount <= 2
                    ? true
                    : false
            },
            retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
        }
    )
}

const useCartLine = (sku: string) => {
    const {
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    const { data: cartData } = useQueryCart()

    const createCart = usePostCart()
    const createCartLine = usePostCartLine()
    const updateCartLine = useMutationCartLine()

    if (!cart_id || !cart_access_token) {
        return { ...createCart, mutateAsync: ({ item }: CartItemBody) => createCart.mutateAsync({ items: [item] }) }
    }

    const item = cartData?.items.find(i => i.product.sku === sku)

    if (item) {
        return {
            ...updateCartLine,
            mutateAsync: (body: CartItemBody) => updateCartLine.mutateAsync({ id: item.id, body }),
        }
    } else {
        return {
            ...createCartLine,
            mutateAsync: (body: CartItemBody) => createCartLine.mutateAsync(body),
        }
    }
}

const useCart = () => {
    const {
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    const createCart = usePostCart()
    const updateCart = useMutationCart()

    if (!cart_id || !cart_access_token) {
        return createCart
    }

    return updateCart
}

const usePostCartCoupon = () => {
    const queryClient = useQueryClient()
    const { language } = useLocalesState()
    const {
        client,
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    return useMutation(
        ["mutateCart", { id: cart_id }],
        (body: CartVoucherBody) =>
            client.updateCartVoucher(cart_id as string, body, { access_token: cart_access_token }, language),
        {
            onSuccess: data => {
                data.items.forEach(i => {
                    eeAddToCart(i, i.quantity, i.price.currency.name, data)
                })
                queryClient.setQueryData(["getCart", { id: cart_id }], data)
            },
            retry: (failureCount, err) => {
                const error = err as APIError
                return error.type !== "ValidationError" &&
                    error.type !== "AccessError" &&
                    failureCount <= 2 &&
                    error.status !== 404
                    ? true
                    : false
            },
            retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 30 * 1000),
        }
    )
}

const useCartCoupon = () => {
    const {
        state: { cart_id, cart_access_token },
    } = useAPIClient()

    const createCart = usePostCart()
    const createCartCoupon = usePostCartCoupon()

    if (!cart_id || !cart_access_token) {
        return { ...createCart, mutateAsync: (body: CartVoucherBody) => createCart.mutateAsync({ voucher: body.code }) }
    }

    return createCartCoupon
}

export {
    useQueryCart,
    useQueryHistoricCart,
    useMutationCart,
    useMutationCartLine,
    useDeleteCartLine,
    useCartLine,
    useCart,
    useCartCoupon,
}
